import { useEffect, useState, memo } from "react";
import { Box, Typography } from "@mui/material";
import AddCircle from "../assets/icons/add-circle.svg";
import CloseCircle from "../assets/icons/close-circle.svg";
import RangeSlider from "../components/RangeSlider";
import SmallTextInput from "../components/SmallTextInput";
import { lightBlackColor } from "../constants/colors";
import TextInput from "../components/TextInput";
import ImagePreview from "../components/ImagePreview";
import Switcher from "../components/Switcher";

const Settings = ({
  videoLength,
  // setVideoLength,
  setCaptionMain,
  captionMain,
  currentTemplate,
  modifications,
  handleModificationChange,
  setModifications,
}) => {
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [audioKeyValue, setAudioKeyValue] = useState(null);
  const [tiktokReplyTemplate, setTiktokReplyTemplate] = useState(null);
  const [avatarKeyValue, setAvatarKeyValue] = useState(null);
  const [emojiKeyValues, setEmojiKeyValues] = useState([]);

  const getTemplateKeyValues = () => {
    let audioKeyValue = null;
    let tiktokReplyTemplateKeyValue = null;
    let avatarKeyValue = null;
    let emojiKeyValues = [];
    currentTemplate?.layersInfo.forEach((layer) => {
      Object.entries(layer).forEach(([sourceKey, objectsArray]) => {
        if (audioKeyValue) return;
        for (const obj of objectsArray) {
          if (obj.type.includes("audio")) {
            audioKeyValue = obj.value;
            break;
          } else {
            audioKeyValue = null;
          }
        }
      });
      Object.entries(layer).forEach(([sourceKey, objectsArray]) => {
        if (tiktokReplyTemplateKeyValue) return;
        if (sourceKey === "tiktokReplyTemplate") {
          tiktokReplyTemplateKeyValue = objectsArray;
        }
      });
      Object.entries(layer).forEach(([sourceKey, objectsArray]) => {
        if (avatarKeyValue) return;
        if (sourceKey === "avatar") {
          avatarKeyValue = objectsArray[0].value;
        }
      });
      Object.entries(layer).forEach(([sourceKey, objectsArray]) => {
        if (sourceKey.includes("emoji")) {
          emojiKeyValues.push({ ...objectsArray[0], composition: sourceKey });
        }
      });
    });
    setAudioKeyValue(audioKeyValue);
    setTiktokReplyTemplate(tiktokReplyTemplateKeyValue);
    setAvatarKeyValue(avatarKeyValue);
    setEmojiKeyValues(emojiKeyValues);
  };

  useEffect(() => {
    if (currentTemplate?.layersInfo?.length) {
      getTemplateKeyValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate?.layersInfo]);

  const handleCaptionMainChange = (value, key) => {
    setCaptionMain((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCaptionMainRangeChange = (values) => {
    if (
      values[0] > 59 ||
      values[1] > 59 ||
      values[0] < 0 ||
      values[1] < 0 ||
      isNaN(values[0]) ||
      isNaN(values[1]) ||
      values[0].length > 2 ||
      values[1].length > 2
    )
      return;
    setCaptionMain((prev) => ({
      ...prev,
      startCutTimecode: values[0],
      endCutTimecode: values[1],
    }));
  };

  const updateLayer = (
    resolutionKey,
    layerType,
    layerValue,
    startCutTimecode,
    endCutTimecode
  ) => {
    setModifications((prevModifications) => {
      const newModifications = { ...prevModifications };

      if (!newModifications[resolutionKey]) {
        newModifications[resolutionKey] = {
          layers: [
            {
              type: layerType,
              value: layerValue,
              startCutTimecode,
              endCutTimecode,
            },
          ],
        };
      } else {
        const layers = newModifications[resolutionKey].layers;
        const layerIndex = layers.findIndex(
          (layer) => layer.type === layerType && layer.value === layerValue
        );

        if (layerIndex === -1) {
          layers.push({
            type: layerType,
            value: layerValue,
            startCutTimecode,
            endCutTimecode,
          });
        } else {
          layers[layerIndex] = {
            ...layers[layerIndex],
            ...(startCutTimecode !== undefined && { startCutTimecode }),
            ...(endCutTimecode !== undefined && { endCutTimecode }),
          };
        }
      }

      return newModifications;
    });
  };

  const handleTTCommentCutChange = (event, values) => {
    updateLayer(
      "tiktokReplyTemplate",
      "text",
      "caption1",
      values[0],
      values[1]
    );
  };

  // const handleChangeRangeFinalVideoLength = (event, values: number[]) => {
  //   const rightValue = values[1];
  //   setVideoLength(rightValue);
  // };

  const handleMainCaptionChange = (event, values: string[]) => {
    handleCaptionMainRangeChange(values);
  };

  // const handleChangeFinalVideoLength = (val) => {
  //   if (+val > 59 || +val < 0 || isNaN(+val) || val.length > 2) return;
  //   setVideoLength(val);
  // };

  const getTabHeight = () => {
    if (
      audioKeyValue &&
      tiktokReplyTemplate &&
      avatarKeyValue &&
      emojiKeyValues?.length
    ) {
      return `${500 + emojiKeyValues.length * 103}px`;
    } else if (
      tiktokReplyTemplate &&
      avatarKeyValue &&
      emojiKeyValues?.length
    ) {
      return `${435 + emojiKeyValues.length * 103}px`;
    } else if (audioKeyValue) {
      return "226px";
    }
  };

  return (
    <Box
      sx={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#000000",
        paddingX: "16px",
        paddingY: "14px",
        backgroundColor: "#ffffff",
        width: "100%",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        onClick={() => setIsOpenSettings((prev) => !prev)}
      >
        <Typography
          sx={{
            color: "rgba(110,90,180, 1)",
            fontWeight: "500",
            fontSize: "36px",
            marginRight: "12px",
          }}
        >
          SETTINGS
        </Typography>
        {isOpenSettings ? (
          <img
            src={CloseCircle}
            alt="close-circle"
            style={{ width: "35px", height: "35px" }}
          />
        ) : (
          <img
            src={AddCircle}
            alt="add-circle"
            style={{ width: "35px", height: "35px" }}
          />
        )}
      </Box>
      <Box
        sx={{
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          marginBottom: isOpenSettings ? "10px" : "0px",
          marginTop: isOpenSettings ? "10px" : "0px",
          transition: "all 0.5s ease",
          height: isOpenSettings ? getTabHeight() : "0px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            sx={{
              color: lightBlackColor,
              fontWeight: "500",
              fontSize: "20px",
              marginRight: "20px",
            }}
          >
            Final Length
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            {/* <Box sx={{ width: "170px", marginRight: "20px" }}>
              <RangeSlider
                onChange={handleChangeRangeFinalVideoLength}
                min={0}
                max={59}
                step={1}
                value={[0, videoLength]}
                disabled={[0]}
              />
            </Box> */}
            <SmallTextInput
              containerStyle={{
                marginBottom: "17px",
              }}
              disabled
              additionalLabel={"s"}
              label={"END"}
              value={videoLength}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextInput
            label={"Main Caption"}
            placeholder={"Enter text"}
            inputStyle={{
              width: "355px",
            }}
            value={captionMain.text}
            onChange={(val) => handleCaptionMainChange(val, "text")}
          />
          <Box sx={{ width: "170px", marginRight: "20px", marginLeft: "20px" }}>
            <RangeSlider
              onChange={handleMainCaptionChange}
              min={0}
              max={59}
              step={1}
              value={[captionMain.startCutTimecode, captionMain.endCutTimecode]}
            />
          </Box>
          <SmallTextInput
            value={captionMain.startCutTimecode}
            onChange={(val) =>
              handleMainCaptionChange(null, [val, captionMain.endCutTimecode])
            }
            label={"START"}
            containerStyle={{
              marginBottom: "11px",
            }}
          />
          <SmallTextInput
            value={captionMain.endCutTimecode}
            onChange={(val) =>
              handleMainCaptionChange(null, [captionMain.startCutTimecode, val])
            }
            label={"END"}
            containerStyle={{
              marginBottom: "11px",
              marginLeft: "8px",
              marginRight: "14px",
            }}
          />
          <Switcher
            value={captionMain.visibility}
            onChange={(val) => handleCaptionMainChange(val, "visibility")}
          />
        </Box>
        <Box>
          {audioKeyValue && (
            <TextInput
              labelStyle={{
                width: "250px",
              }}
              label={`Audio Track (${
                currentTemplate?.name.includes("T01") ||
                currentTemplate?.name.includes("T33")
                  ? "50"
                  : "100"
              }% volume)`}
              containerStyle={{
                marginBottom: "35px",
              }}
              placeholder={"*.mp4, *.wav link"}
              value={
                modifications?.["sourceFootage1920x1080"]?.layers?.find(
                  (layer) => {
                    const valueName = currentTemplate?.name.includes("merge")
                      ? "musicTrack"
                      : audioKeyValue;

                    return layer.type === "audio" && layer.value === valueName;
                  }
                )?.newValue
              }
              onChange={(val) => {
                const valueName = currentTemplate.name.includes("merge")
                  ? "musicTrack"
                  : audioKeyValue;

                handleModificationChange(
                  val,
                  "sourceFootage1920x1080",
                  "newValue",
                  "audio",
                  valueName
                );
                handleModificationChange(
                  val,
                  "sourceFootage800x1000",
                  "newValue",
                  "audio",
                  valueName
                );
                handleModificationChange(
                  val,
                  "sourceFootage1080x1920",
                  "newValue",
                  "audio",
                  valueName
                );
              }}
            />
          )}
          {tiktokReplyTemplate && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "35px" }}>
              <TextInput
                labelStyle={{
                  width: "130px",
                }}
                label={"TT Username"}
                inputStyle={{
                  width: "355px",
                }}
                placeholder={"reply to..."}
                value={
                  modifications?.["tiktokReplyTemplate"]?.layers?.find(
                    (layer) =>
                      layer.type === "text" && layer.value === "caption2"
                  )?.newValue
                }
                onChange={(val) => {
                  handleModificationChange(
                    val,
                    "tiktokReplyTemplate",
                    "newValue",
                    "text",
                    "caption2"
                  );
                }}
              />
              <TextInput
                labelStyle={{
                  width: "130px",
                }}
                containerStyle={{
                  marginBottom: "5px",
                }}
                label={"TT Comment"}
                inputStyle={{
                  width: "355px",
                }}
                placeholder={"Commment"}
                value={
                  modifications?.["tiktokReplyTemplate"]?.layers?.find(
                    (layer) =>
                      layer.type === "text" && layer.value === "caption1"
                  )?.newValue
                }
                onChange={(val) => {
                  handleModificationChange(
                    val,
                    "tiktokReplyTemplate",
                    "newValue",
                    "text",
                    "caption1"
                  );
                }}
              />
            </Box>
          )}
          {avatarKeyValue && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextInput
                labelStyle={{
                  width: "120px",
                }}
                label={"Avatar link"}
                placeholder={"*.png, *.jpeg *.webp"}
                value={
                  modifications?.["avatar"]?.layers?.find(
                    (layer) =>
                      layer.type === "image" && layer.value === avatarKeyValue
                  )?.newValue
                }
                onChange={(val) => {
                  handleModificationChange(
                    val,
                    "avatar",
                    "newValue",
                    "image",
                    avatarKeyValue
                  );
                }}
              />
              <Typography
                sx={{
                  color: lightBlackColor,
                  fontSize: "20px",
                  fontWeight: "400",
                  fontStyle: "italic",
                  marginLeft: "20px",
                  marginRight: "60px",
                }}
              >
                square image*
              </Typography>
              <ImagePreview
                imageUrl={
                  modifications?.["avatar"]?.layers?.find(
                    (layer) =>
                      layer.type === "image" && layer.value === avatarKeyValue
                  )?.newValue
                }
              />
            </Box>
          )}
          {!!emojiKeyValues?.length && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {emojiKeyValues
                ?.sort((a, b) => a?.composition.localeCompare(b?.composition))
                .map((emojiKeyValue, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "6px",
                    }}
                  >
                    <TextInput
                      label={`Emoji link ${index + 1}`}
                      labelStyle={{
                        width: "120px",
                      }}
                      placeholder={"*.png, *.jpeg *.webp"}
                      value={
                        modifications?.[
                          emojiKeyValue.composition
                        ]?.layers?.find(
                          (layer) =>
                            layer.type === "image" &&
                            layer.value === emojiKeyValue.value
                        )?.newValue
                      }
                      onChange={(val) => {
                        handleModificationChange(
                          val,
                          emojiKeyValue.composition,
                          "newValue",
                          "image",
                          emojiKeyValue.value
                        );
                      }}
                    />
                    <Typography
                      sx={{
                        color: lightBlackColor,
                        fontSize: "20px",
                        fontWeight: "400",
                        fontStyle: "italic",
                        marginLeft: "20px",
                        marginRight: "60px",
                      }}
                    >
                      square image*
                    </Typography>
                    <ImagePreview
                      imageUrl={
                        modifications?.[
                          emojiKeyValue.composition
                        ]?.layers?.find(
                          (layer) =>
                            layer.type === "image" &&
                            layer.value === emojiKeyValue.value
                        )?.newValue
                      }
                    />
                  </Box>
                ))}
            </Box>
          )}
          {!!tiktokReplyTemplate?.length && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  color: lightBlackColor,
                  fontWeight: "500",
                  fontSize: "20px",
                  marginRight: "20px",
                }}
              >
                TT comment CUT
              </Typography>
              <Box
                sx={{ width: "170px", marginRight: "20px", marginLeft: "20px" }}
              >
                <RangeSlider
                  onChange={handleTTCommentCutChange}
                  min={0}
                  max={59}
                  step={1}
                  value={[
                    modifications?.["tiktokReplyTemplate"]?.layers?.find(
                      (layer) =>
                        layer.type === "text" && layer.value === "caption1"
                    )?.startCutTimecode,
                    modifications?.["tiktokReplyTemplate"]?.layers?.find(
                      (layer) =>
                        layer.type === "text" && layer.value === "caption1"
                    )?.endCutTimecode,
                  ]}
                />
              </Box>
              <SmallTextInput
                value={
                  modifications?.["tiktokReplyTemplate"]?.layers?.find(
                    (layer) =>
                      layer.type === "text" && layer.value === "caption1"
                  )?.startCutTimecode
                }
                onChange={(val) =>
                  handleModificationChange(
                    val,
                    "tiktokReplyTemplate",
                    "startCutTimecode",
                    "text",
                    "caption1"
                  )
                }
                label={"START"}
                containerStyle={{
                  marginBottom: "11px",
                }}
              />
              <SmallTextInput
                value={
                  modifications?.["tiktokReplyTemplate"]?.layers?.find(
                    (layer) =>
                      layer.type === "text" && layer.value === "caption1"
                  )?.endCutTimecode
                }
                onChange={(val) =>
                  handleModificationChange(
                    val,
                    "tiktokReplyTemplate",
                    "endCutTimecode",
                    "text",
                    "caption1"
                  )
                }
                label={"END"}
                containerStyle={{
                  marginBottom: "11px",
                  marginLeft: "8px",
                  marginRight: "14px",
                }}
              />
              <Switcher
                value={
                  modifications?.["tiktokReplyTemplate"]?.layers?.find(
                    (layer) =>
                      layer.type === "text" && layer.value === "caption1"
                  )?.visibilty
                }
                onChange={(val) =>
                  handleModificationChange(
                    val,
                    "tiktokReplyTemplate",
                    "visibilty",
                    "text",
                    "caption1"
                  )
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default memo(Settings);
